import { useEffect } from "react";
import { useFormikContext } from "formik";
import { useBlocker } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";

interface Props {
  message?: string;
}

export default function FormUnsavedChangesGuard({
  message = "You have unsaved changes or required fields left empty. Are you sure you want to leave this page?"
}: Props) {
  const { dirty, isValid, errors, validateForm } = useFormikContext();
  const { t } = useTranslation();

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const shouldBlock = dirty || !isValid;
  const blocker = useBlocker(shouldBlock);

  const open = blocker.state === "blocked";

  // @ts-ignore
  const handleConfirm = () => blocker.proceed();
  // @ts-ignore
  const handleCancel = () => blocker.reset();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldBlock) return;
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock, message]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{t('Unsaved Changes')}</DialogTitle>
      <DialogContent>{t(message)}</DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t('Stay')}
        </Button>
        <Button onClick={handleConfirm} color="error">
          {t('Leave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}